import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

export type FilterOption = { value: string; label: string };

/**
 * Controlled search + filter toolbar. The consumer owns state (typically via useSearchParams);
 * this component only debounces keystrokes before calling onSearchChange with the settled value.
 */
export function TableToolbar({
  searchValue, onSearchChange, searchPlaceholder = 'Search…',
  statuses, statusValue = '', onStatusChange,
  filters, primaryAction, onClear
}: {
  searchValue: string; onSearchChange: (value: string) => void; searchPlaceholder?: string;
  statuses?: FilterOption[]; statusValue?: string; onStatusChange?: (value: string) => void;
  filters?: ReactNode; primaryAction?: ReactNode; onClear?: () => void;
}) {
  const [draft, setDraft] = useState(searchValue);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => setDraft(searchValue), [searchValue]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const schedule = (value: string) => {
    setDraft(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearchChange(value), 300);
  };
  const submit = (event: FormEvent) => { event.preventDefault(); clearTimeout(timer.current); onSearchChange(draft); };
  const hasActiveFilters = Boolean(searchValue || statusValue);
  const clear = () => { clearTimeout(timer.current); setDraft(''); onSearchChange(''); onStatusChange?.(''); onClear?.(); };

  return <form className="bv-table-toolbar" role="search" onSubmit={submit}>
    <label className="bv-table-toolbar__search">
      <Search aria-hidden="true" size={16} />
      <input className="bv-input" name="q" value={draft} onChange={(event) => schedule(event.target.value)} placeholder={searchPlaceholder} aria-label="Search" />
    </label>
    {statuses && statuses.length > 0 && (
      <select className="bv-select" name="status" value={statusValue} aria-label="Status" onChange={(event) => onStatusChange?.(event.target.value)}>
        <option value="">All statuses</option>
        {statuses.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    )}
    {filters}
    {hasActiveFilters && <button className="bv-button bv-button--quiet" type="button" onClick={clear}><X aria-hidden="true" size={16} />Clear</button>}
    <div className="bv-table-toolbar__spacer" />
    {primaryAction}
  </form>;
}

/** @deprecated use TableToolbar with controlled state; kept for any remaining uncontrolled callers. */
export function FilterBar({ statuses = [], onChange }: { statuses?: FilterOption[]; onChange?: (query: URLSearchParams) => void }) {
  const current = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
  const [search, setSearch] = useState(current.get('q') ?? '');
  const [status, setStatus] = useState(current.get('status') ?? '');
  const write = (nextSearch: string, nextStatus: string) => {
    const query = new URLSearchParams(window.location.search);
    if (nextSearch) query.set('q', nextSearch); else query.delete('q');
    if (nextStatus) query.set('status', nextStatus); else query.delete('status');
    const suffix = query.toString();
    window.history.pushState({}, '', `${window.location.pathname}${suffix ? `?${suffix}` : ''}${window.location.hash}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
    onChange?.(query);
  };
  return <TableToolbar
    searchValue={search} onSearchChange={(value) => { setSearch(value); write(value, status); }}
    searchPlaceholder="Search records"
    statuses={statuses} statusValue={status} onStatusChange={(value) => { setStatus(value); write(search, value); }}
  />;
}
